import { inject } from '@angular/core'
import { Route, Router } from '@angular/router'
import { canActivateAuthGuardFn, canDeactivateDirtyFormGuard } from '@saniup-webapps/shared'
import { LoginComponent, NotFoundComponent } from '@saniup-webapps/shared/components'
import { externalPartnerGuard, leistungserbringerGuard } from './guards/employee.guard'
import { HIMI_TAB, himiTabUrl } from './dashboard/himi-versorgung/himi-versorgung-tabs'
import { NavigationComponent } from './navigation/navigation.component'

function buildTitle(title: string): string {
  return `${title} | SaniUp ONE`
}

export const appRoutes: Route[] = [
  {
    path: 'login',
    component: LoginComponent,
    title: buildTitle('Login'),
  },
  // Main App
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'landing',
  },
  {
    path: '',
    component: NavigationComponent,
    title: 'SaniUp ONE',
    data: {
      title: '',
    },
    canActivate: [canActivateAuthGuardFn()],
    children: [
      {
        // Hier lief bis zur Freigabe am 2026-09-28 die neue Startseite als Vorschau. Die Umleitung
        // hält alte Lesezeichen am Leben, statt sie auf „Nicht gefunden“ laufen zu lassen.
        path: 'landing/neu',
        redirectTo: 'landing',
      },
      {
        path: 'landing',
        loadComponent: () => import('./landing-page/landing-page.component').then((m) => m.LandingPageComponent),
      },
      {
        path: '',
        canActivate: [leistungserbringerGuard],
        children: [
          // Patient
          {
            path: 'patient',
            redirectTo: 'dashboard/patient',
            pathMatch: 'full',
          },
          {
            path: 'patient/anlage',
            loadComponent: () => import('./patient/patient-anlage/patient-anlage.component').then((m) => m.PatientAnlageComponent),
            canDeactivate: [canDeactivateDirtyFormGuard],
            title: buildTitle('Patienten'),
          },
          {
            path: 'patient/anlage/:patientenId',
            loadComponent: () => import('./patient/patient-edit/patient-edit.component').then((m) => m.PatientEditComponent),
            canDeactivate: [canDeactivateDirtyFormGuard],
            title: buildTitle('Patienten'),
          },
          {
            path: 'patient/:patientenId',
            loadComponent: () => import('./patient/patient-detail/patient-detail.component').then((m) => m.PatientDetailComponent),
            title: buildTitle('Patienten'),
          },
          // Verkauf (Selbstzahler)
          {
            path: 'verkauf/anlage',
            loadComponent: () =>
              import('./verkauf/verkauf-anlage-stepper-page/verkauf-anlage-stepper-page.component').then(
                (m) => m.VerkaufAnlageStepperPageComponent,
              ),
            // Anders als der HiMi-Assistent daneben: der hier speichert NICHTS zwischendurch, also
            // ist der Guard die einzige Rettung vor Datenverlust — und unsere Nutzer:innen
            // navigieren mit der Browser-Zurueck-Taste.
            canDeactivate: [canDeactivateDirtyFormGuard],
            title: buildTitle('Neuer Verkauf'),
          },
          // HimiVersorgung
          {
            path: 'himi-versorgung/anlage',
            loadComponent: () =>
              import('./himi-versorgung/himi-versorgung-create-draft-page/himi-versorgung-create-draft-page.component').then(
                (m) => m.HimiVersorgungCreateDraftPageComponent,
              ),
            title: buildTitle('Neue Versorgung'),
          },
          {
            path: 'himi-versorgung/anlage/:draftId',
            loadComponent: () =>
              import('./himi-versorgung/himi-versorgung-create-stepper-page/himi-versorgung-create-stepper-page.component').then(
                (m) => m.HimiVersorgungCreateStepperPageComponent,
              ),
            title: buildTitle('Neue Versorgung'),
            // Der Assistent stellt seinen EIGENEN Ausstiegs-Dialog statt des generischen
            // „Aenderungen verwerfen?" (`askToDiscardForm()` der Stepper-Seite): der Stand ist durch
            // das Autosave laengst gespeichert, zu entscheiden ist nur, ob er als Entwurf sichtbar
            // bleibt, still verschwindet oder geloescht wird. Gefragt wird auch nur dann — wer
            // einen bestehenden Entwurf oder eine Versorgung bloss aufmacht und wieder rausgeht,
            // sieht nichts (`isDirty()`). Siehe
            // `.claude/rules/leistungserbringer-webapp/himi-versorgung.md`.
            canDeactivate: [canDeactivateDirtyFormGuard],
          },
          // Bemusterung — noch nicht fuer Kunden freigegeben, deshalb auf SaniUp-Mitarbeitende
          // gesperrt. Ausblenden allein genuegt nicht: die Seiten waeren ueber ihre URL weiter
          // erreichbar. Dasselbe Flag steuert die Weiche im PG15-Assistenten und die Einstiege in
          // der Hilfsmittel-Seite und der Patientenakte.
          // Eigene Aufgaben (Backend docs/claude/aufgaben.md): fester Ort in der Seitenleiste
          {
            path: 'aufgaben',
            loadComponent: () => import('./aufgaben/aufgaben-page.component').then((m) => m.AufgabenPageComponent),
            title: buildTitle('Aufgaben'),
          },
          // dashboard
          {
            path: 'dashboard/patient',
            loadComponent: () => import('./dashboard/patient/patient-page/patient-page.component').then((m) => m.PatientPageComponent),
            title: buildTitle('Patienten Dashboard'),
          },
          {
            path: 'dashboard/phm',
            loadComponent: () =>
              import('./dashboard/phm-versorgung/phm-versorgung-page/phm-versorgung-page.component').then(
                (m) => m.PhmVersorgungPageComponent,
              ),
            title: buildTitle('Pflegehilfsmittel Dashboard'),
          },
          {
            path: 'dashboard/himi-versorgung',
            loadComponent: () =>
              import('./dashboard/himi-versorgung/himi-versorgung-page/himi-versorgung-page.component').then(
                (m) => m.HimiVersorgungPageComponent,
              ),
            title: buildTitle('Hilfsmittel Dashboard'),
          },
          // Entwürfe und Bemusterungen sind seit 2026-08-19 **Reiter** der Hilfsmittel-Seite und keine
          // eigenen Seiten mehr (Ansage Tim). Ihre alten Pfade bleiben als Weiterleitung auf den
          // passenden Reiter bestehen — sie stehen in Lesezeichen, in verschickten Links und im
          // Assistenten hinter „Entwurf speichern".
          // Die Weiterleitung MUSS eine Funktion sein: ein `redirectTo`-String kann keinen
          // Query-Parameter setzen, und der Reiter haengt genau daran.
          {
            path: 'dashboard/himi-versorgung/entwuerfe',
            redirectTo: () => inject(Router).parseUrl(himiTabUrl(HIMI_TAB.ENTWUERFE)),
            pathMatch: 'full',
          },
          {
            path: 'dashboard/himi-versorgung/bemusterungen',
            redirectTo: () => inject(Router).parseUrl(himiTabUrl(HIMI_TAB.BEMUSTERUNGEN)),
            pathMatch: 'full',
          },
          // Die Vorgangsseite einer einzelnen Bemusterung. Sie lag hier bis 2026-08-11 schon einmal
          // und ist am 2026-08-13 zurueckgekommen — Begruendung im Klassenkommentar der Komponente.
          // Sie bleibt eine eigene Seite: ein Vorgang mit eigener URL, kein Reiter — und sie MUSS
          // unter `dashboard/himi-versorgung/` liegen. Das ist das Einzige, was „Hilfsmittel" in der
          // Seitenleiste markiert hält, solange man darauf steht (`routerLinkActive` in
          // `lib-navigation-button` prüft ohne `exact`, matcht also Kind-Pfade mit).
          {
            path: 'dashboard/himi-versorgung/bemusterung/:bemusterungId',
            loadComponent: () =>
              import('./bemusterung/bemusterung-detail-page/bemusterung-detail-page.component').then(
                (m) => m.BemusterungDetailPageComponent,
              ),
            title: buildTitle('Bemusterung'),
          },
          // Noch aeltere Pfade aus der Zeit mit eigenem Nav-Eintrag — direkt auf den Reiter, statt
          // ueber die Weiterleitung darueber: eine Kette aus zwei Weiterleitungen loest der Router
          // zwar auf, aber der Zwischenschritt taucht in der History auf.
          {
            path: 'dashboard/entwuerfe',
            redirectTo: () => inject(Router).parseUrl(himiTabUrl(HIMI_TAB.ENTWUERFE)),
            pathMatch: 'full',
          },
          {
            path: 'dashboard/bemusterungen',
            redirectTo: () => inject(Router).parseUrl(himiTabUrl(HIMI_TAB.BEMUSTERUNGEN)),
            pathMatch: 'full',
          },
          {
            path: 'dashboard/verkaeufe',
            loadComponent: () => import('./dashboard/verkauf/verkauf-page/verkauf-page.component').then((m) => m.VerkaufPageComponent),
            title: buildTitle('Verkäufe'),
          },
          {
            path: 'dashboard/ekv',
            loadComponent: () => import('./dashboard/monitoring/ekv-page/ekv-page.component').then((m) => m.EkvPageComponent),
            title: buildTitle('EKV Dashboard'),
          },
          {
            path: 'dashboard/monitoring',
            loadComponent: () => import('./dashboard/monitoring/monitoring.component').then((m) => m.MonitoringComponent),
            title: buildTitle('Monitoring Dashboard'),
          },
          {
            path: 'dashboard/abrechnung',
            loadComponent: () =>
              import('./dashboard/abrechnung/abrechnung-page/abrechnung-page.component').then((m) => m.AbrechnungPageComponent),
            title: buildTitle('Abrechnung Dashboard'),
            // Der DATEV-Tab haelt ungespeicherte Einstellungen — ohne den Guard sind sie beim
            // Klick in die Seitenleiste still weg.
            canDeactivate: [canDeactivateDirtyFormGuard],
          },
          {
            path: 'dashboard/abrechnung/einnahmen',
            loadComponent: () =>
              import('./dashboard/abrechnung/abrechnung-page/einnahmen-dashboard/einnahmen-dashboard.component').then(
                (m) => m.EinnahmenDashboardComponent,
              ),
            title: buildTitle('Einnahmen Report'),
          },
          {
            path: 'dashboard/abrechnung/:id',
            loadComponent: () =>
              import('./dashboard/abrechnung/abrechnung-page/abrechnung-detail/abrechnung-detail.component').then(
                (m) => m.AbrechnungDetailComponent,
              ),
          },
          {
            path: 'dashboard/abrechnung/:id/produktgruppe/:productGroup',
            loadComponent: () =>
              import('./dashboard/abrechnung/abrechnung-page/abrechnung-detail/pg-detail/pg-detail.component').then(
                (m) => m.PgDetailComponent,
              ),
            title: buildTitle('Produktgruppen-Details'),
          },
          {
            path: 'dashboard/abrechnung/:id/rezept-versand',
            loadComponent: () =>
              import('./dashboard/abrechnung/abrechnung-page/abrechnung-detail/rezept-versand-wizard/rezept-versand-wizard.component').then(
                (m) => m.RezeptVersandWizardComponent,
              ),
            canDeactivate: [canDeactivateDirtyFormGuard],
            title: buildTitle('Verordnungsversand-Wizard'),
          },
          {
            path: 'dashboard/abrechnung/:id/abrechnungsbereit',
            loadComponent: () =>
              import('./dashboard/abrechnung/abrechnung-page/abrechnung-detail/abrechnungsbereite-rezepte-list/abrechnungsbereite-rezepte-list.component').then(
                (m) => m.AbrechnungsbereiteRezepteListComponent,
              ),
            title: buildTitle('Abrechnungsbereite Verordnungen'),
          },
          {
            path: 'dashboard/abrechnung/:id/nicht-abrechnungsbereit',
            loadComponent: () =>
              import('./dashboard/abrechnung/abrechnung-page/abrechnung-detail/nicht-abrechnungsbereite-rezepte-list/nicht-abrechnungsbereite-rezepte-list.component').then(
                (m) => m.NichtAbrechnungsbereiteRezepteListComponent,
              ),
            title: buildTitle('Nicht abrechnungsbereite Verordnungen'),
          },
          // phm
          {
            path: 'phm/anlage',
            loadComponent: () => import('./phm/phm-patient-select/phm-patient-select.component').then((m) => m.PhmPatientSelectComponent),
            title: buildTitle('PHM Versorgung'),
          },
          // Anlage OHNE bestehenden Patienten: das Patientenportal erfasst die Stammdaten selbst und
          // legt den Patienten beim Absenden an. MUSS vor `phm/anlage/:patientId` stehen — sonst
          // matcht der Platzhalter und „neu" landet als Patienten-Id in der Abfrage.
          {
            path: 'phm/anlage/neu',
            loadComponent: () => import('./phm/phm-iframe/phm-iframe.component').then((m) => m.PhmIframeComponent),
            title: buildTitle('PHM Versorgung'),
          },
          {
            path: 'phm/anlage/:patientId',
            loadComponent: () => import('./phm/phm-iframe/phm-iframe.component').then((m) => m.PhmIframeComponent),
            title: buildTitle('PHM Versorgung'),
          },

          {
            path: 'admin',
            loadComponent: () => import('./admin-page/admin-page.component').then((m) => m.AdminPageComponent),
            title: buildTitle('Einstellungen'),
          },
          /* Preview */
          {
            path: 'preview/cards',
            loadComponent: () => import('./preview/cards-preview/cards-preview.component').then((m) => m.CardsPreviewComponent),
            title: buildTitle('Preview Cards'),
          },
          {
            path: 'preview/scan',
            loadComponent: () => import('./scan/scan-page/scan-page.component').then((m) => m.ScanPageComponent),
            title: buildTitle('Dokument scannen'),
          },
          {
            path: 'preview/true-scale',
            loadComponent: () =>
              import('./preview/true-scale-preview/true-scale-preview.component').then((m) => m.TrueScalePreviewComponent),
            title: buildTitle('PDF 1:1 Maßstab'),
          },
        ],
      },
      {
        path: 'ep',
        canActivate: [externalPartnerGuard],
        children: [
          {
            path: 'dashboard/patient',
            loadComponent: () =>
              import('./external-partner/dashboard/patient-page/patient-page.component').then((m) => m.PatientPageComponent),
            title: buildTitle('Patienten Dashboard'),
          },
        ],
      },
      // Management
      {
        path: 'profile',
        loadComponent: () => import('./profil/profil.component').then((m) => m.ProfilComponent),
        title: buildTitle('Profil'),
      },
      {
        path: '**',
        component: NotFoundComponent,
      },
    ],
  },
]
